// SPDX-License-Identifier: Elastic-2.0
/**
 * useA5SkipAccountless — shared "Skip — set up later" handler for A5
 * (F-348, evolves the former `useA5LocalOnly`).
 *
 * Extracted into its own module so both consumers — the body's
 * right-column skip button AND the `OnboardingPage` footer secondary —
 * can import just the hook without pulling in the whole A5Account
 * component tree. Also satisfies the `react-refresh/only-export-components`
 * lint rule (no non-component exports from a component-default file).
 *
 * Behaviour (per ADR-002 Amendment §A.1 — account-less Free):
 *   1. `loadDeviceId()` — obtain the install's anonymous device_id from
 *      the gateway (the add-on owns it). NO token is minted; NO account
 *      is created.
 *   2. `useAuth.setAccountLess(deviceId)` — record account-less Free.
 *   3. `useOnboardingStore.setAuth({ mode: "local-only", paired: false })`.
 *
 * This replaces the former local-JWT issuance (F-300 `LocalTokenIssuer`,
 * only ever mocked). The hook stays low-level: it does NOT advance the
 * wizard (`setStep`) — that's the caller's UI concern, which keeps the
 * hook usable from both the body button and the footer wire-up.
 */
import { useCallback, useState } from "react";

import { useAuth } from "@/api/auth-store";
import { loadDeviceId } from "@/api/deviceId";

import { useOnboardingStore } from "../useOnboardingStore";

/**
 * Result of `useA5SkipAccountless`. Low-level by design so both the body
 * button and the `OnboardingPage` footer can compose it.
 */
export interface UseA5SkipAccountlessResult {
  /**
   * Has the account-less outcome already been written to the auth +
   * onboarding stores? Mirrors `useOnboardingStore.auth.mode ===
   * "local-only"` for the caller's convenience.
   */
  selected: boolean;
  /** In-flight indicator for the device_id load. */
  loading: boolean;
  /** Last error message from the device_id load, if any. */
  error: string | null;
  /**
   * Imperatively run the account-less outcome (see module docstring).
   * Idempotent — calling it again while still loading is a no-op.
   *
   * @returns `true` on success, `false` on failure.
   */
  runSkip: () => Promise<boolean>;
}

export function useA5SkipAccountless(): UseA5SkipAccountlessResult {
  const setAccountLess = useAuth((s) => s.setAccountLess);
  const setOnboardingAuth = useOnboardingStore((s) => s.setAuth);
  const mode = useOnboardingStore((s) => s.auth.mode);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const runSkip = useCallback(async (): Promise<boolean> => {
    if (loading) {
      return false;
    }
    setLoading(true);
    setError(null);
    try {
      const deviceId = await loadDeviceId();
      setAccountLess(deviceId);
      setOnboardingAuth({ mode: "local-only", paired: false });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "device_id load failed";
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [loading, setAccountLess, setOnboardingAuth]);

  return {
    selected: mode === "local-only",
    loading,
    error,
    runSkip,
  };
}
